/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Operations › Overview, the console's landing page, rebuilt on the kit (overhaul plan step 3a). Same two calls as the
 * old Dashboard (GET /v1/admin/dashboard and GET /v1/admin/config) and the same fields; what changed is how they read:
 *  - a failed load is said, in the service's words, with Try again (it used to leave a spinner, then nothing);
 *  - spend against the fleet caps is shown (the response always carried `budget` and `spend`);
 *  - ledger, history-storage, voice and setup warnings are rows in Service health, so the first screen keeps its shape
 *    instead of growing a card per warning above the numbers;
 *  - one money, count and token format (ui/format.ts), numbers right-aligned in tabular figures.
 */
import { useCallback, useEffect, useState } from 'react';
import { Activity as ActivityIcon, ArrowUpRight, Boxes, CircleSlash, Coins, Cpu, Info, RefreshCw } from 'lucide-react';
import { api, type ConfigStatus, type Dashboard, type DashAppRow, type DashModelRow, type LedgerState } from '../lib/api';
import { historyStorageWarning, type HistoryStorageState } from '../lib/historyStorage';
import { unmetHeadline, unmetRows, type UnmetRow } from '../lib/unmet';
import { linkClick } from '../shell/router';
import {
  Button, Callout, EmptyState, ErrorState, Grid, HealthList, HealthRow, Icon, Kpi, LinkButton, MeterRow, PageHeader, Panel,
  Segmented, ShareBar, Skeleton, Stack, StatusChip, Table, Tag, failureText, type Column, type HealthTone, type Tone,
} from '../ui';
import { count, date, money, nextUtcMidnight, percent, plural, ratio, time, tokens } from '../ui/format';

type Load<T> = { state: 'loading' } | { state: 'ready'; data: T } | { state: 'failed'; error: unknown };

const mib = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MiB`;

/** Bar colour for a fill against a cap: amber from 80% (when the threshold event fires), red at the cap. */
function capTone(value: number): 'data' | 'warn' | 'bad' {
  if (value >= 1) return 'bad';
  if (value >= 0.8) return 'warn';
  return 'data';
}

interface Health {
  key: string;
  tone: HealthTone;
  title: string;
  sub?: string;
  chip?: { tone: Tone; text: string };
  meter?: { value: number; label: string };
}

function ledgerHealth(key: string, name: string, what: string, ledger: LedgerState): Health {
  if (!ledger.hydrated) {
    return {
      key, tone: 'warn', title: `${name} not loaded`,
      sub: `The service could not read its stored ${what} (${plural(ledger.hydrateAttempts, 'attempt')} so far; it keeps retrying). Until a read succeeds the caps count only the turns served since this process started, so they trip later than configured. Nothing is written over the stored ledger meanwhile.`,
      chip: { tone: 'warn', text: 'Partial data' },
    };
  }
  if (ledger.flushFailures > 0) {
    return {
      key, tone: 'warn', title: `${name} cannot be written`,
      sub: `The latest figures are held in memory; ${plural(ledger.flushFailures, 'write')} failed. It retries every 15 seconds, and nothing is lost unless the service restarts first.`,
      chip: { tone: 'warn', text: `${plural(ledger.flushFailures, 'failure')}` },
    };
  }
  return { key, tone: 'ok', title: name, sub: 'Read from storage · every save written', chip: { tone: 'ok', text: 'Healthy' } };
}

function historyHealth(state: HistoryStorageState | undefined): Health | null {
  if (!state) return null;
  if (state.backend === 'keyed') return { key: 'history', tone: 'ok', title: 'History storage', sub: 'Saved chats are kept in the database', chip: { tone: 'ok', text: 'Healthy' } };
  const warning = historyStorageWarning(state);
  const max = state.maxDocumentBytes;
  const used = max && state.documentBytes !== undefined ? ratio(state.documentBytes, max) : null;
  const sizes = max && state.documentBytes !== undefined
    ? `${mib(state.documentBytes)} of its ${mib(max)} limit, across ${plural(state.owners ?? 0, 'person-and-app list')}`
    : undefined;
  if (warning?.level === 'alert') {
    return { key: 'history', tone: 'bad', title: warning.title, sub: warning.text, chip: { tone: 'bad', text: used === null ? 'Not readable' : percent(used) } };
  }
  if (warning) return { key: 'history', tone: 'warn', title: warning.title, sub: sizes ?? warning.text, chip: { tone: 'warn', text: used === null ? 'Nearly full' : percent(used) } };
  return { key: 'history', tone: 'ok', title: 'History storage', sub: sizes ?? 'Saved chats are stored', chip: { tone: 'ok', text: 'Healthy' } };
}

function setupHealth(dash: Dashboard, cfg: Load<ConfigStatus>): Health | null {
  if (cfg.state === 'loading') return null;
  if (cfg.state === 'failed') {
    return { key: 'setup', tone: 'bad', title: 'Setup could not be checked', sub: `The settings could not be read. The service said: ${failureText(cfg.error)}`, chip: { tone: 'bad', text: 'Not read' } };
  }
  const c = cfg.data;
  const steps = [
    { done: c.hasAnthropicKey, required: true, label: 'Add the Anthropic API key (Settings › Model & key)' },
    { done: Boolean(c.model), required: true, label: 'Choose a model (Settings › Model & key)' },
    { done: dash.appsEnabled > 0, required: true, label: 'Switch on at least one app (Settings › Apps & surfaces)' },
    { done: c.hasElevenKey, required: false, label: 'Configure a voice (Settings › Voice), optional' },
    { done: Boolean(c.budget?.dailyUsd || c.budget?.monthlyUsd), required: false, label: 'Set a spend cap (Operations › Caps & limits), optional' },
  ];
  const left = steps.filter((s) => !s.done);
  if (left.length === 0) return null;
  const required = left.filter((s) => s.required).length;
  return {
    key: 'setup',
    tone: required ? 'warn' : 'neutral',
    title: 'Get Gyro ready',
    sub: left.map((s) => s.label).join(' · '),
    chip: required ? { tone: 'warn', text: `${required} required left` } : { tone: 'neutral', text: `${left.length} optional left` },
  };
}

function healthRows(dash: Dashboard, cfg: Load<ConfigStatus>): Health[] {
  const rows: Health[] = [];
  const setup = setupHealth(dash, cfg);
  if (setup) rows.push(setup);
  if (dash.ledgers) {
    rows.push(ledgerHealth('spend', 'Spend ledger', 'spend ledger', dash.ledgers.spend));
    rows.push(ledgerHealth('quota', 'Usage quota ledger', 'usage quota ledger', dash.ledgers.usageQuota));
  } else {
    rows.push({ key: 'ledgers', tone: 'neutral', title: 'Spend and quota ledgers', sub: 'Not reported by this service version', chip: { tone: 'neutral', text: 'Unknown' } });
  }
  const history = historyHealth(dash.historyStorage);
  if (history) rows.push(history);
  if (dash.voice) {
    const used = ratio(dash.voice.characterCount, dash.voice.characterLimit);
    rows.push({
      key: 'voice', tone: used >= 1 ? 'bad' : used >= 0.8 ? 'warn' : 'ok', title: 'Voice (ElevenLabs)',
      sub: `${count(dash.voice.characterCount)} of ${count(dash.voice.characterLimit)} characters${dash.voice.tier ? ` · ${dash.voice.tier} tier` : ''}`,
      meter: { value: used, label: `ElevenLabs characters, ${percent(used)} used` },
    });
  } else if (dash.voiceStatus === 'usage_unreadable') {
    // A key is stored but its usage could not be read (owner 2026-10-31): the row never says "no key" for it.
    rows.push({
      key: 'voice', tone: 'warn', title: 'Voice (ElevenLabs)',
      sub: "Key set; usage could not be read (the key may lack ElevenLabs' User read permission). Spoken replies still work if the key is valid.",
      chip: { tone: 'warn', text: 'Usage unknown' },
    });
  } else {
    rows.push({ key: 'voice', tone: 'neutral', title: 'Voice (ElevenLabs)', sub: "No ElevenLabs key: Gyro uses the browser's own voice", chip: { tone: 'neutral', text: 'Not set up' } });
  }
  return rows;
}

function SpendPanel({ dash }: { dash: Dashboard }) {
  const spend = dash.spend;
  const dayCap = dash.budget?.dailyUsd && dash.budget.dailyUsd > 0 ? dash.budget.dailyUsd : null;
  const monthCap = dash.budget?.monthlyUsd && dash.budget.monthlyUsd > 0 ? dash.budget.monthlyUsd : null;
  const reset = nextUtcMidnight(dash.updatedAt);
  const row = (label: string, spent: number, cap: number | null, period: string, footEnd: string) => {
    const used = cap ? ratio(spent, cap) : null;
    return (
      <MeterRow
        label={label}
        figure={cap ? <><strong>{money(spent)}</strong> <span className="ui-quiet">of {money(cap)}</span></> : <><strong>{money(spent)}</strong> <span className="ui-quiet">· no cap set</span></>}
        value={used}
        meterLabel={`${period} spend, ${used === null ? 'no cap' : `${percent(used)} of the cap`}`}
        tone={used === null ? 'data' : capTone(used)}
        footStart={used === null ? 'No cap: nothing pauses Gyro' : `${percent(used)} used`}
        footEnd={footEnd}
      />
    );
  };
  return (
    <Panel
      title="Spend against fleet caps"
      description="Every app together, cache and web search included"
      actions={<LinkButton variant="ghost" size="sm" href="/caps" iconAfter={ArrowUpRight} onClick={(event) => linkClick(event)}>Caps &amp; limits</LinkButton>}
      footer={<><Icon icon={Info} size="sm" /><span>At 80% a <code>usage.threshold</code> event fires; at 100% Gyro pauses everywhere and names the cap.</span></>}
    >
      {spend ? (
        <div className="ui-stack is-tight">
          {row('Today (UTC)', spend.day, dayCap, "Today's", reset ? `Resets ${time(reset, { zone: true })}` : 'Resets at midnight UTC')}
          {row('Last 30 days', spend.month, monthCap, 'Last 30 days', 'Rolling window')}
        </div>
      ) : (
        <EmptyState compact icon={Coins} title="Spend is not reported">This service version does not send fleet spend with the overview.</EmptyState>
      )}
    </Panel>
  );
}

function HealthPanel({ dash, cfg }: { dash: Dashboard; cfg: Load<ConfigStatus> }) {
  const rows = healthRows(dash, cfg);
  const problems = rows.filter((r) => r.tone === 'warn' || r.tone === 'bad');
  const worst: Tone = problems.some((r) => r.tone === 'bad') ? 'bad' : 'warn';
  return (
    <Panel
      flush
      title="Service health"
      description="What the service reports about its own storage"
      actions={problems.length
        ? <StatusChip tone={worst}>{plural(problems.length, worst === 'bad' ? 'problem' : 'warning')}</StatusChip>
        : <StatusChip tone="ok">All healthy</StatusChip>}
    >
      <HealthList label="Service health">
        {rows.map((r) => (
          <HealthRow
            key={r.key}
            tone={r.tone}
            title={r.title}
            sub={r.sub}
            side={r.meter
              ? <span className="ui-health-meter"><ShareBar value={r.meter.value} label={r.meter.label} digits={0} /></span>
              : r.chip ? <StatusChip tone={r.chip.tone}>{r.chip.text}</StatusChip> : null}
          />
        ))}
      </HealthList>
    </Panel>
  );
}

type ShareBy = 'cost' | 'requests';

function AppsPanel({ dash, loading }: { dash: Dashboard | null; loading: boolean }) {
  const [shareBy, setShareBy] = useState<ShareBy>('cost');
  const rows = dash?.perApp ?? [];
  const totalErrors = rows.reduce((sum, row) => sum + (row.errors || 0), 0);
  const share = (row: DashAppRow) => shareBy === 'cost' ? ratio(row.costUsd, dash?.total.costUsd) : ratio(row.requests, dash?.total.requests);
  const columns: Array<Column<DashAppRow>> = [
    { key: 'app', header: 'App', cell: (row) => <Tag>{row.app}</Tag> },
    { key: 'requests', header: 'Requests', numeric: true, cell: (row) => count(row.requests) },
    { key: 'input', header: 'Input', numeric: true, hideOnPhone: true, cell: (row) => tokens(row.inputTokens) },
    { key: 'output', header: 'Output', numeric: true, hideOnPhone: true, cell: (row) => tokens(row.outputTokens) },
    { key: 'errors', header: 'Errors', numeric: true, cell: (row) => count(row.errors) },
    { key: 'cost', header: 'Est. cost', numeric: true, cell: (row) => money(row.costUsd) },
    { key: 'share', header: shareBy === 'cost' ? 'Share of cost' : 'Share of requests', width: '200px', hideOnPhone: true, cell: (row) => <ShareBar value={share(row)} label={`${row.app}: share of ${shareBy}`} /> },
  ];
  return (
    <Panel
      flush
      title="Usage by app"
      description="Requests, tokens and estimated cost per calling app"
      actions={<Segmented label="Share of" value={shareBy} onChange={setShareBy} options={[{ value: 'cost', label: 'Cost' }, { value: 'requests', label: 'Requests' }]} />}
    >
      <Table
        label="Usage by app"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.app}
        loading={loading}
        skeletonRows={5}
        total={dash ? {
          app: 'Total', requests: count(dash.total.requests), input: tokens(dash.total.inputTokens), output: tokens(dash.total.outputTokens),
          errors: count(totalErrors), cost: money(dash.total.costUsd),
        } : undefined}
        phone={{
          title: (row) => <Tag>{row.app}</Tag>,
          meta: (row) => `${plural(row.requests, 'request')} · ${plural(row.errors, 'error')}`,
          value: (row) => money(row.costUsd),
          sub: (row) => percent(share(row), 1),
        }}
        phoneTotal={dash ? { title: 'Total', meta: `${plural(dash.total.requests, 'request')} · ${plural(totalErrors, 'error')}`, value: money(dash.total.costUsd) } : undefined}
        empty={<EmptyState icon={Boxes} title="No usage yet">Once apps start asking Gyro, requests, tokens and cost per app appear here.</EmptyState>}
      />
    </Panel>
  );
}

function ModelsPanel({ dash, loading }: { dash: Dashboard | null; loading: boolean }) {
  const columns: Array<Column<DashModelRow>> = [
    { key: 'model', header: 'Model', mono: true, cell: (row) => row.model },
    { key: 'requests', header: 'Requests', numeric: true, cell: (row) => count(row.requests) },
    { key: 'tokens', header: 'Tokens', numeric: true, hideOnPhone: true, cell: (row) => tokens(row.inputTokens + row.outputTokens) },
    { key: 'cost', header: 'Est. cost', numeric: true, cell: (row) => money(row.costUsd) },
  ];
  return (
    <Panel flush title="Usage by model" description="Same period">
      <Table stacked
        label="Usage by model"
        columns={columns}
        rows={dash?.byModel ?? []}
        rowKey={(row) => row.model}
        loading={loading}
        skeletonRows={3}
        empty={<EmptyState compact icon={Cpu} title="No model usage yet">Each model Gyro answers with is listed here with its requests and cost.</EmptyState>}
      />
    </Panel>
  );
}

function UnmetPanel({ dash, loading }: { dash: Dashboard | null; loading: boolean }) {
  const unmet = dash?.unmet;
  const rows = unmetRows(unmet);
  const columns: Array<Column<UnmetRow>> = [
    { key: 'category', header: 'Asked for', cell: (row) => row.category },
    { key: 'kind', header: 'Why not', muted: true, cell: (row) => row.kind },
    { key: 'count', header: 'Count', numeric: true, cell: (row) => count(row.count) },
    { key: 'share', header: 'Share', width: '132px', hideOnPhone: true, cell: (row) => <ShareBar value={row.share / 100} label={`${row.category}: share`} digits={0} /> },
  ];
  const description = unmet && unmet.total > 0
    ? `${plural(unmet.total, 'request')} in the last ${unmet.windowDays} days · counts only, no names or words`
    : 'Counts only, no names or words';
  return (
    <Panel
      flush
      title="What Gyro could not do"
      description={description}
      actions={unmet && !unmet.hydrated ? <StatusChip tone="warn">Stored counts not loaded</StatusChip> : null}
      footer={<><Icon icon={Info} size="sm" /><span>Gyro&apos;s own report when it declines, and connector failures for a system that is not connected or lacks a permission. The roadmap works from this table.</span></>}
    >
      <Table stacked
        label="What Gyro could not do"
        columns={columns}
        rows={rows}
        rowKey={(row) => `${row.category}|${row.kind}`}
        loading={loading}
        skeletonRows={4}
        empty={<EmptyState compact icon={CircleSlash} title={unmet ? 'Nothing counted' : 'Not reported'}>{unmetHeadline(unmet)}</EmptyState>}
      />
    </Panel>
  );
}

export function Overview() {
  const [dash, setDash] = useState<Load<Dashboard>>({ state: 'loading' });
  const [cfg, setCfg] = useState<Load<ConfigStatus>>({ state: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<unknown>(null);

  const load = useCallback(async (initial: boolean) => {
    if (!initial) setRefreshing(true);
    setRefreshError(null);
    const config = api.getConfig().then(
      (data) => setCfg({ state: 'ready', data }),
      (error: unknown) => setCfg({ state: 'failed', error }),
    );
    try {
      const data = await api.dashboard();
      setDash({ state: 'ready', data });
    } catch (error) {
      // A failed refresh keeps the figures on screen and says so; a failed first load replaces them.
      setDash((current) => (current.state === 'ready' ? current : { state: 'failed', error }));
      setRefreshError(error);
    } finally {
      await config;
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
  }, [load]);

  const data = dash.state === 'ready' ? dash.data : null;
  const loading = dash.state === 'loading';
  const errors = data ? data.perApp.reduce((sum, row) => sum + (row.errors || 0), 0) : 0;
  const appsOff = data ? Math.max(0, data.surfaces.length - data.appsEnabled) : 0;

  return (
    <>
      <PageHeader
        meta={data ? <span>Since {date(data.since)} · updated {time(data.updatedAt)}</span> : loading ? <Skeleton width="180px" /> : null}
        actions={<Button icon={RefreshCw} onClick={() => void load(false)} busy={refreshing} aria-label="Refresh" className="is-collapse-phone">Refresh</Button>}
      />
      <div className="ui-page">
        {dash.state === 'failed' ? (
          <ErrorState title="The overview could not be loaded" error={dash.error} onRetry={() => void load(false)} retrying={refreshing} />
        ) : (
          <Stack>
            {refreshError && data ? (
              <Callout tone="bad" role="alert" title="Refresh failed">
                The service said: {failureText(refreshError)} These figures are from {time(data.updatedAt)}.
              </Callout>
            ) : null}
            <Grid cols={4} label="Headline figures">
              <Kpi icon={Coins} label="Total cost (est.)" loading={loading} value={data ? money(data.total.costUsd) : null} sub="Anthropic list pricing" />
              <Kpi
                icon={ActivityIcon}
                label="Requests"
                loading={loading}
                value={data ? count(data.total.requests) : null}
                sub={data ? `${plural(errors, 'error')} · ${percent(ratio(errors, data.total.requests), 1)}` : null}
              />
              <Kpi
                icon={Cpu}
                label="Tokens"
                loading={loading}
                value={data ? tokens(data.total.inputTokens + data.total.outputTokens) : null}
                sub={data ? `${tokens(data.total.inputTokens)} in · ${tokens(data.total.outputTokens)} out` : null}
              />
              <Kpi
                icon={Boxes}
                label="Apps active"
                loading={loading}
                value={data ? count(data.appsEnabled) : null}
                suffix={data ? `of ${count(data.surfaces.length)}` : null}
                sub={data ? (appsOff ? `${plural(appsOff, 'app')} switched off` : 'Every app is switched on') : null}
              />
            </Grid>

            {data ? (
              <Grid cols={2}>
                <SpendPanel dash={data} />
                <HealthPanel dash={data} cfg={cfg} />
              </Grid>
            ) : (
              <Grid cols={2}>
                <Panel title="Spend against fleet caps" description="Every app together, cache and web search included">
                  <div className="ui-stack is-tight" aria-hidden="true"><Skeleton size="md" /><Skeleton size="md" /></div>
                </Panel>
                <Panel title="Service health" description="What the service reports about its own storage">
                  <div className="ui-stack is-tight" aria-hidden="true"><Skeleton size="md" /><Skeleton size="md" /><Skeleton size="md" /></div>
                </Panel>
              </Grid>
            )}

            <AppsPanel dash={data} loading={loading} />

            <Grid cols={2}>
              <ModelsPanel dash={data} loading={loading} />
              <UnmetPanel dash={data} loading={loading} />
            </Grid>
          </Stack>
        )}
      </div>
    </>
  );
}
